"use client";
import { Button } from "../components/ui/Button";
import EmptyState from "../components/EmptyState";
import LazyImage from "../components/ui/LazyImage";
import NewsSection from "../components/NewsSection";
import { toPersianDate } from "../utils/dateFormatter";
import staticLogo from "../assets/images/green-logo.png";
import { highlightText } from "../utils/TextHighLighter";
import ProjectGallery from "../components/ProjectGallery";
import { Link, useOutletContext } from "react-router-dom";
import MondayMeetingsList from "../components/MondayMeetingsList";
import { mapPersonNewsToNews } from "../utils/mapPersonNewsToNews";
import { usePodcasts } from "../features/podcast/hooks/usePodcasts";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useSinglePerson } from "../features/home/hooks/useSinglePerson";
import { useFollowUser } from "../features/managers/hooks/useFollowUser";
import { useFollowCheck } from "../features/managers/hooks/useFollowCheck";
import { useSingleProject } from "../features/home/hooks/useSingleProject";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/Tabs";

type MainTabKey =
  | "introduction"
  | "news"
  | "achievements"
  | "mission"
  | "monday-meetings"
  | "Meetings"
  | "trips"
  | "visits";

type LayoutContextType = {
  setHideHeaderOnScroll: (v: boolean) => void;
  setMobileBottomNavContent: (content: ReactNode | null) => void;
  currentTrackId?: number;
  isPlaying: boolean;
  onSelectTrack: (trackId: number) => void;
  isMediaCollapsed: boolean;
  setIsMediaCollapsed: (v: boolean) => void;
  isMediaPlaying: boolean;
  setTracks: (tracks: any[]) => void;
  onTogglePlay: () => void;
  setIsMediaPlaying: (v: boolean | ((p: boolean) => boolean)) => void;
};

const MAIN_TABS = [
  { value: "introduction", label: "معرفی" },
  { value: "news", label: "اخبار" },
  { value: "achievements", label: "خدمات" },
  { value: "mission", label: "بعثه" },
  { value: "monday-meetings", label: "جلسات دوشنبه" },
  { value: "Meetings", label: "ملاقات ها" },
  { value: "trips", label: "سفرها" },
  { value: "visits", label: "بازدیدها" },
] as const satisfies ReadonlyArray<{ value: MainTabKey; label: string }>;

export default function Founder() {
  const projectId = 1008;
  const [mainTab, setMainTab] = useState<MainTabKey>("introduction");
  const [isFollowing, setIsFollowing] = useState<boolean>(false);
  const [searchQuery] = useState("");
  const [expandedTab, setExpandedTab] = useState<
    "introduction" | "content" | null
  >(null);
  const expandTab = (tab: "introduction" | "content") => {
    setExpandedTab(tab);
  };
  const isIntroExpanded = expandedTab === "introduction";
  const isWorksExpanded = expandedTab === "content";
  const [, setShowMobileStickyHeader] = useState(false);
  const {
    currentTrackId,
    isPlaying,
    onSelectTrack,
    setIsMediaCollapsed,
    setTracks,
    onTogglePlay,
  } = useOutletContext<LayoutContextType>();

  const handleSelectTrackFromList = (episodeId: number) => {
    onSelectTrack(episodeId);
    setIsMediaCollapsed(false);
  };

  const { toggleFollowUser, loading: userFollowLoading } = useFollowUser();
  const { data: project } = useSingleProject(projectId);
  const managerFromProject = project?.persons?.[1] ?? project?.persons?.[0];
  const managerId = managerFromProject?.id;
  const { data: personRes } = useSinglePerson(managerId as number);
  const personData = personRes?.data;
  const personNews = useMemo(
    () => (personData ? mapPersonNewsToNews(personData, personData.news) : []),
    [personData],
  );
  const { data: checkedUserFollowing, isFetching: userCheckLoading } =
    useFollowCheck("persons", managerId);
  const managerName = managerFromProject?.name ?? "";
  const managerNickname = managerFromProject?.nickname ?? "";
  const introductionHtml =
    project?.introduction ?? project?.description ?? project?.content ?? "";
  const servicesHtml = project?.services ?? "";
  const { data: podcastsRes } = usePodcasts();
  const podcasts = podcastsRes?.data;
  const tracks = useMemo(() => {
    return (
      podcasts
        ?.map((item) => {
          const src = item.files?.[0]?.url;

          if (!src || typeof src !== "string" || !src.trim()) {
            return null;
          }

          return {
            id: item.id,
            title: item.name,
            subtitle: item.meta_text ?? "",
            src: src.trim(),
            cover: item.cover ?? null,
            durationLabel: item.duration ?? "",
            date: item.date,
          };
        })
        .filter((t): t is NonNullable<typeof t> => t !== null) ?? []
    );
  }, [podcasts]);
  useEffect(() => {
    setTracks(tracks);
  }, [tracks, setTracks]);

  const currentTrack = tracks.find((t) => t.id === currentTrackId) || null;

  useEffect(() => {
    if (typeof checkedUserFollowing === "boolean") {
      setIsFollowing(checkedUserFollowing);
    }
  }, [checkedUserFollowing]);

  const handleToggleFollowUser = async () => {
    if (!managerId) return;
    const res = await toggleFollowUser(managerId);
    if (!res) return;
    setIsFollowing(res.isFollowing);
  };

  useEffect(() => {
    const ENTER_THRESHOLD = 500;
    const handleScroll = () => {
      const isMobile = window.innerWidth < 1280;
      if (!isMobile) {
        setShowMobileStickyHeader(false);
        return;
      }
      setShowMobileStickyHeader(window.scrollY > ENTER_THRESHOLD);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  return (
    <>
      <div className="w-full mt-5 xl:m-0 pb-1 px-4">
        <div className="xl:hidden flex flex-col items-start justify-start bg-white p-3 shadow-sm rounded-[20px] overflow-hidden">
          <div className="flex items-center gap-x-2.5 w-full">
            <LazyImage
              src={project?.logo || staticLogo}
              alt={project?.name || ""}
              className="size-[70px] border border-green-02 object-cover rounded-[20px] shrink-0"
            />
            <Link to={`/institutions/${project?.id}`} className="min-w-0">
              <div className="flex flex-col justify-center gap-y-0.5">
                {project?.categories?.[0]?.name && (
                  <span className="text-[11px] text-green-04 font-normal">
                    {project.categories[0].name}
                  </span>
                )}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="line-clamp-1 text-[15px] font-bold text-green-05">
                        {project?.name}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent className="bg-green-02.5 text-green-05">
                      <p>{project?.name}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                {project?.date_establishment && (
                  <span className="text-[11px] text-green-04 font-normal">
                    {toPersianDate(project.date_establishment)}
                  </span>
                )}
              </div>
            </Link>
          </div>

          <div className="flex items-center gap-x-2 w-full mt-2.5 bg-green-02/50 p-2 rounded-2xl">
            <LazyImage
              src={managerFromProject?.logo || staticLogo}
              alt={managerName || ""}
              className="size-11 object-cover !border-[1px] border-green-0 shrink-0 rounded-[10px]"
            />
            <Link
              to={managerId ? `/managers/${managerId}` : "#"}
              className="min-w-0 space-y-1"
            >
              <p className="text-[10px] font-medium text-green-04/80">
                {managerNickname}
              </p>
              <p className="text-xs font-bold text-green-05 truncate">
                {managerName}
              </p>
            </Link>
          </div>

          <Button
            onClick={handleToggleFollowUser}
            disabled={userFollowLoading || userCheckLoading || !managerId}
            className={`w-full flex items-center justify-center gap-2 text-sm font-medium mt-2.5 rounded-xl border-none transition-all
               ${
                 isFollowing
                   ? "bg-green-02 text-green-04 hover:bg-green-02/70 hover:text-green-04/90"
                   : "text-white bg-green-05"
               }`}
          >
            {userFollowLoading || userCheckLoading ? (
              <span className="size-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
            ) : isFollowing ? (
              "دنبال میکنید"
            ) : (
              "دنبال کردن"
            )}
          </Button>
        </div>
      </div>

      <Tabs
        className="flex flex-col !justify-start w-full min-h-screen bg-green-01 pb-4 xl:pb-12"
        value={mainTab}
        onValueChange={(val) => setMainTab(val as MainTabKey)}
      >
        <div className="w-screen xl:w-full flex justify-center items-center mt-5 xl:my-10">
          <TabsList
            dir="rtl"
            className="mx-4 xl:m-0 h-[49px] xl:h-full xl:w-fit border border-white bg-gradient-to-b from-green-02/25 to-white/50 overflow-x-auto py-2 px-2.5 xl:px-10 space-x-2 no-scrollbar xl:space-x-5 rounded-[20px]"
          >
            {MAIN_TABS.map((tab) => (
              <TabsTrigger
                key={tab.value}
                value={tab.value}
                className="flex-1 h-[29px] xl:h-11 text-[13px] text-green-05 font-medium data-[state=active]:text-green-05 data-[state=active]:bg-complement-02 data-[state=active]:rounded-[10px] data-[state=active]:to-[#B9E94822] !border-none data-[state=active]:shadow-[0_0_4px_rgba(0,0,0,0.03)] shadow-none px-4 xl:px-7 xl:!rounded-[20px]"
              >
                {highlightText(tab.label, searchQuery)}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        <div className="xl:flex w-full items-start justify-between gap-x-5 space-y-2 xl:space-y-0 px-4 pb-4 xl:pb-0 xl:mb-36 2xl:px-11 text-right">
          <main className="w-full basis-[80%] rounded-[20px]">
            <div className="mt-0 text-right text-green-05 focus-visible:ring-0 overflow-hidden rounded-[24px]">
              <TabsContent
                value="introduction"
                dir="rtl"
                className="relative bg-white p-4 xl:p-10 min-h-fit overflow-hidden"
              >
                {!introductionHtml ? (
                  <EmptyState />
                ) : (
                  <div
                    className={`estedad3 leading-7 transition-all duration-300 ${!isIntroExpanded ? "max-h-[13rem] overflow-hidden" : ""}`}
                  >
                    <div
                      dangerouslySetInnerHTML={{ __html: introductionHtml }}
                    />
                  </div>
                )}
                <div className="xl:hidden">
                  <ProjectGallery
                    images={(personData?.images || []).map((url) => ({ url }))}
                  />
                </div>
                {!isIntroExpanded && introductionHtml && (
                  <div className="absolute bottom-0 left-0 w-full h-28 bg-gradient-to-t from-white via-white/85 to-transparent" />
                )}
                {!isIntroExpanded && introductionHtml && (
                  <div className="absolute bottom-4 left-0 w-full flex justify-center z-10">
                    <button
                      onClick={() => expandTab("introduction")}
                      className="text-green-04 text-[10px] px-3 h-7 bg-green-02 rounded-lg shadow-sm"
                    >
                      بیشتر
                    </button>
                  </div>
                )}
              </TabsContent>

              <TabsContent
                value="news"
                dir="rtl"
                className="mt-0 border-none bg-white focus-visible:ring-0 overflow-hidden"
              >
                <NewsSection
                  form="form1"
                  layout="grid"
                  showHeader={false}
                  showEmptyState
                  newsData={personNews}
                />
              </TabsContent>

              <TabsContent
                value="achievements"
                dir="rtl"
                className="relative bg-white p-4 xl:p-10 min-h-fit overflow-hidden"
              >
                {!servicesHtml ? (
                  <EmptyState />
                ) : (
                  <div
                    className={`estedad3 leading-7 transition-all duration-300 ${!isWorksExpanded ? "max-h-[13rem] overflow-hidden" : ""}`}
                  >
                    <div dangerouslySetInnerHTML={{ __html: servicesHtml }} />
                  </div>
                )}
                {!isWorksExpanded && servicesHtml && (
                  <div className="absolute bottom-0 left-0 w-full h-28 bg-gradient-to-t from-white via-white/85 to-transparent" />
                )}
                {!isWorksExpanded && servicesHtml && (
                  <div className="absolute bottom-4 left-0 w-full flex justify-center z-10">
                    <button
                      onClick={() => expandTab("content")}
                      className="text-green-04 text-[10px] px-3 h-7 bg-green-02 rounded-lg shadow-sm"
                    >
                      بیشتر
                    </button>
                  </div>
                )}
              </TabsContent>

              <TabsContent
                value="mission"
                className="mt-0 border-none p-0 focus-visible:ring-0"
              >
                <EmptyState />
              </TabsContent>

              <TabsContent value="monday-meetings">
                {tracks?.length > 0 ? (
                  <MondayMeetingsList
                    tracks={tracks}
                    currentTrackId={currentTrack?.id}
                    isPlaying={isPlaying}
                    onSelectTrack={handleSelectTrackFromList}
                    onTogglePlay={onTogglePlay}
                  />
                ) : (
                  <EmptyState />
                )}
              </TabsContent>

              <TabsContent
                value="Meetings"
                className="mt-0 border-none p-0 focus-visible:ring-0"
              >
                <EmptyState />
              </TabsContent>

              <TabsContent
                value="trips"
                className="mt-0 border-none p-0 focus-visible:ring-0"
              >
                <EmptyState />
              </TabsContent>

              <TabsContent
                value="visits"
                className="mt-0 border-none p-0 focus-visible:ring-0"
              >
                <EmptyState />
              </TabsContent>
            </div>
          </main>

          <aside dir="rtl" className="basis-[20%] space-y-2 rounded-[20px]">
            <div className="hidden xl:flex flex-col items-start justify-start bg-white p-3 shadow-sm rounded-[20px] overflow-hidden">
              <div className="flex items-center gap-x-2.5 w-full">
                <LazyImage
                  src={project?.logo || staticLogo}
                  className="size-[70px] border border-green-02 object-cover rounded-[20px] shrink-0"
                  alt={project?.name || ""}
                />
                <Link to={`/institutions/${project?.id}`} className="min-w-0">
                  <div className="flex flex-col justify-center gap-y-0.5">
                    {project?.categories?.[0]?.name && (
                      <span className="text-[11px] text-green-04 font-normal">
                        {project.categories[0].name}
                      </span>
                    )}
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="line-clamp-1 text-[15px] font-bold text-green-05">
                            {project?.name}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent className="bg-green-02.5 text-green-05">
                          <p>{project?.name}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    {project?.date_establishment && (
                      <span className="text-[11px] text-green-04 font-normal">
                        {toPersianDate(project.date_establishment)}
                      </span>
                    )}
                  </div>
                </Link>
              </div>

              <div className="flex items-center gap-x-2 w-full mt-2.5 bg-green-02/50 p-2 rounded-2xl">
                <LazyImage
                  src={managerFromProject?.logo || staticLogo}
                  alt={managerName || ""}
                  className="size-11 object-cover !border-[1px] border-green-0 shrink-0 rounded-[10px]"
                />
                <Link
                  to={managerId ? `/managers/${managerId}` : "#"}
                  className="min-w-0 space-y-1"
                >
                  <p className="text-[10px] font-medium text-green-04/80">
                    {managerNickname}
                  </p>
                  <p className="text-xs font-bold text-green-05 truncate">
                    {managerName}
                  </p>
                </Link>
              </div>

              <Button
                onClick={handleToggleFollowUser}
                disabled={userFollowLoading || userCheckLoading || !managerId}
                className={`w-full flex items-center justify-center gap-2 text-sm font-medium mt-2.5 rounded-xl border-none transition-all
                   ${
                     isFollowing
                       ? "bg-green-02 text-green-04 hover:bg-green-02/70 hover:text-green-04/90"
                       : "text-white bg-green-05"
                   }`}
              >
                {userFollowLoading || userCheckLoading ? (
                  <span className="size-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                ) : isFollowing ? (
                  "دنبال میکنید"
                ) : (
                  "دنبال کردن"
                )}
              </Button>
            </div>
            <div className="hidden xl:block">
              <ProjectGallery
                images={(personData?.images || []).map((url) => ({ url }))}
              />
            </div>
            <div className="!mt-7">
              <NewsSection form="form1" />
            </div>
          </aside>
        </div>
      </Tabs>
    </>
  );
}
